import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AppUpdateDownload = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const role = sessionStorage.getItem("role");

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [config, setConfig] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/app-update/config?platform=android`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Unable to fetch app update details");
      }
      setConfig(data.data);
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Unable to load update details right now.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!config?.apkUrl) {
      setStatus({ type: "error", message: "No APK is available to download right now." });
      return;
    }

    setDownloading(true);
    // Redirects to S3 URL; browser handles actual file download/open.
    window.location.href = config.apkUrl;
    setTimeout(() => setDownloading(false), 1200);
  };

  const dashboardPath = "/EmployeeDashboard";

  if (loading) {
    return (
      <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 2, md: 4 },
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, rgba(241,245,255,0.85) 0%, rgba(255,255,255,1) 50%, rgba(241,245,255,0.5) 100%)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate(dashboardPath)} sx={{ borderRadius: 999 }}>
          Back to Dashboard
        </Button>
        <Button variant="text" onClick={fetchConfig} sx={{ borderRadius: 999 }}>
          Refresh
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            flex: 2,
            p: { xs: 2.5, md: 4 },
            borderRadius: 4,
            border: "1px solid #e8ecff",
            background:
              "radial-gradient(circle at top right, rgba(29,78,216,0.12), rgba(29,78,216,0) 34%), #ffffff",
            boxShadow: "0 16px 40px rgba(17, 24, 39, 0.08)",
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
                App Download Center
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Download the latest Android APK and stay updated with the newest release.
              </Typography>
            </Box>

            {status.message ? (
              <Alert severity={status.type || "info"} sx={{ borderRadius: 2 }}>
                {status.message}
              </Alert>
            ) : null}

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ p: 2.5, border: "1px solid #eef1ff", borderRadius: 3, background: "#fafbff" }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  LATEST VERSION
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  {config?.latestVersion || "Not available"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  RELEASE TYPE
                </Typography>
                <Box sx={{ mt: 0.7 }}>
                  <Chip
                    label={config?.forceUpdate ? "Mandatory Update" : "Optional Update"}
                    color={config?.forceUpdate ? "warning" : "success"}
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Stack>

            <Box
              sx={{
                p: 2.5,
                border: "1px dashed #cbd5ff",
                borderRadius: 3,
                background: "rgba(37, 99, 235, 0.03)",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Release Notes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {config?.releaseNotes?.trim()
                  ? config.releaseNotes
                  : "No release notes available for this version."}
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="contained"
                onClick={handleDownload}
                disabled={downloading || !config?.apkUrl}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 1.2,
                  background: "linear-gradient(90deg, #1d4ed8 0%, #2563eb 100%)",
                }}
              >
                {downloading ? "Preparing download..." : "Download APK"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => config?.apkUrl && window.open(config.apkUrl, "_blank")}
                disabled={!config?.apkUrl}
                sx={{ borderRadius: 999, px: 3, py: 1.2 }}
              >
                Open Download Link
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 4,
            border: "1px solid #e8ecff",
            background:
              "radial-gradient(circle at bottom left, rgba(37,99,235,0.12), rgba(37,99,235,0) 38%), #ffffff",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800}>
              Download Steps
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Follow these quick steps for a smooth update experience.
            </Typography>
            <Chip label="1) Click Download APK" color="primary" variant="outlined" />
            <Chip label="2) Allow file download in browser" color="primary" variant="outlined" />
            <Chip label="3) Install APK from Downloads" color="primary" variant="outlined" />
            <Chip label="4) Open app and verify version" color="primary" variant="outlined" />
            <Typography variant="caption" color="text.secondary">
              If download does not start, use the "Open Download Link" button.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Logged in as: {role || "user"}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default AppUpdateDownload;
